import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useNav } from "../nav.tsx";
import { ErrorNote, errText, formatWhen, SectionTitle } from "../ui.tsx";
import type { StageProps } from "./EmployeeShell.tsx";

type Bump = "auto" | "major" | "minor" | "patch";

export function PublishStage({ api, detail, reload }: StageProps) {
  const { go } = useNav();
  const e = detail.employee;
  const [bump, setBump] = useState<Bump>("auto");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const latest = detail.releases[0];
  const locked = detail.stages.publish === "locked";

  const publish = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await api.publish(e.id, bump === "auto" ? undefined : bump);
      await reload();
      go(`/employees/${e.id}/assign`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const openBundle = async (rid: string) => {
    const b = await api.bundle(rid);
    const url = URL.createObjectURL(new Blob([JSON.stringify(b, null, 2)], { type: "application/json" }));
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="grid gap-4">
      <SectionTitle
        title="Publish"
        description="Publishing freezes the reviewed context, the knowledge and the pinned avatar into an immutable, content-hashed release. Tenants that hire it keep that exact version."
      />
      <ErrorNote error={error} />
      {locked && (
        <p className="text-sm text-muted-foreground">
          {detail.state.draftStale
            ? "The draft is stale. Distill again and review it."
            : !detail.state.reviewed
              ? "Review the draft first."
              : !detail.state.avatarLive
                ? "The pinned avatar is no longer published. Pick another."
                : "Complete the earlier stages first."}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>New release</CardTitle>
          <CardDescription>
            {latest
              ? `The version number is chosen from what changed since v${latest.version}: scope or tools → major, knowledge → minor, anything else → patch. You can raise it, never lower it.`
              : "The first release is v1.0.0."}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {latest && (
            <RadioGroup value={bump} onValueChange={(v) => setBump(v as Bump)} className="flex flex-wrap gap-4">
              {(["auto", "patch", "minor", "major"] as Bump[]).map((b) => (
                <div key={b} className="flex items-center gap-2">
                  <RadioGroupItem value={b} id={`bump-${b}`} />
                  <Label htmlFor={`bump-${b}`}>{b === "auto" ? "Automatic" : b}</Label>
                </div>
              ))}
            </RadioGroup>
          )}
          <div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={busy || locked}>Publish release</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Publish {e.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    A release cannot be edited or deleted afterwards. Later changes become a new version. It is not visible to any tenant until you assign it.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => void publish()}>Publish</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {detail.releases.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Version</TableHead>
              <TableHead>Published</TableHead>
              <TableHead>Content hash</TableHead>
              <TableHead className="text-right">Bundle</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.releases.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">v{r.version}</TableCell>
                <TableCell>{formatWhen(r.publishedAt)}</TableCell>
                <TableCell className="font-mono text-xs">{r.bundleSha256.slice(0, 16)}…</TableCell>
                <TableCell className="text-right">
                  <Button size="xs" variant="ghost" onClick={() => void openBundle(r.id)}>
                    View JSON
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
